
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { getYoutubeEmbedUrl, getVimeoEmbedUrl, normalizeYoutubeId, normalizeVimeoId } from '@/utils/videoEmbed';
import { VideoConfig } from '@/types/videoConfig';

interface CountryVideoFieldsProps {
  videoConfig: VideoConfig | null | undefined;
  onChange: (videoConfig: VideoConfig) => void;
}

export const CountryVideoFields: React.FC<CountryVideoFieldsProps> = ({ 
  videoConfig, 
  onChange 
}) => {
  const [config, setConfig] = useState<VideoConfig>({
    enabled: false,
    platform: 'youtube',
    video_id: '',
    title: '',
  });
  
  // Initialize from existing config or defaults
  useEffect(() => {
    if (videoConfig) {
      setConfig({ 
        enabled: videoConfig.enabled || false,
        platform: videoConfig.platform || 'youtube',
        video_id: videoConfig.video_id || '',
        title: videoConfig.title || ''
      });
    }
  }, [videoConfig]);
  
  const handleChange = <K extends keyof VideoConfig>(key: K, value: VideoConfig[K]) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    onChange(newConfig);
  };
  
  const handleVideoIdChange = (value: string) => {
    let normalizedId = value;
    
    // Try to extract ID from URLs based on platform
    if (config.platform === 'youtube') {
      normalizedId = normalizeYoutubeId(value);
    } else if (config.platform === 'vimeo') {
      normalizedId = normalizeVimeoId(value);
    }
    
    handleChange('video_id', normalizedId);
  };
  
  // Get embed preview URL based on platform and ID
  const getPreviewUrl = () => {
    if (!config.video_id) return '';
    
    if (config.platform === 'youtube') {
      return getYoutubeEmbedUrl(config.video_id);
    } else {
      return getVimeoEmbedUrl(config.video_id);
    }
  };
  
  const previewUrl = getPreviewUrl();
  
  return (
    <div className="space-y-6 py-4 border-t pt-4">
      <h3 className="text-lg font-medium">Featured Video</h3>
      
      {/* Enable/Disable Switch */}
      <div className="flex items-center justify-between">
        <Label htmlFor="video-enabled">Enable featured video</Label>
        <Switch
          id="video-enabled"
          checked={config.enabled}
          onCheckedChange={(checked) => handleChange('enabled', checked)}
        />
      </div>
      
      {/* Platform Selection */}
      <div className="space-y-2">
        <Label>Platform</Label>
        <RadioGroup 
          value={config.platform} 
          onValueChange={(value) => handleChange('platform', value as 'youtube' | 'vimeo')}
          className="flex space-x-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="youtube" id="youtube" />
            <Label htmlFor="youtube">YouTube</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="vimeo" id="vimeo" />
            <Label htmlFor="vimeo">Vimeo</Label>
          </div>
        </RadioGroup>
      </div>
      
      {/* Video ID / URL Input */}
      <div className="space-y-2">
        <Label htmlFor="video-id">Video ID or URL</Label>
        <Input
          id="video-id"
          value={config.video_id}
          onChange={(e) => handleVideoIdChange(e.target.value)}
          placeholder={config.platform === 'youtube' ? "YouTube Video ID or URL" : "Vimeo Video ID or URL"}
          disabled={!config.enabled}
        />
        <p className="text-xs text-muted-foreground">
          {config.platform === 'youtube' 
            ? "Example: https://www.youtube.com/watch?v=VIDEO_ID or just VIDEO_ID" 
            : "Example: https://vimeo.com/VIDEO_ID or just VIDEO_ID"}
        </p>
      </div>
      
      {/* Video Title */}
      <div className="space-y-2">
        <Label htmlFor="video-title">Video Title</Label>
        <Input
          id="video-title"
          value={config.title}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="Enter video title"
          disabled={!config.enabled}
        />
      </div>
      
      {/* Video Preview */}
      {config.enabled && config.video_id && (
        <div className="space-y-2">
          <Label>Preview</Label>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              title="Video Preview"
              className="absolute inset-0 w-full h-full"
              src={previewUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};
